// --- Imports ---
import React from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import { OpenInNew, GitHub, Code as CodeIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

// --- Live Project Card ---
export default function LiveProjectCard({ project, idx }) {
  const navigate = useNavigate();
  const theme = useTheme();

  const accentColor = project?.color || theme?.palette?.primary?.main;

  return (
    <Stack
      className={`project-${idx}`}
      sx={{
        width: "100vw",
        height: "100vh",
        position: "relative",
        padding: { xs: 2, md: 6, lg: 8 },
        justifyContent: "center",
      }}
    >
      {/* --- Ambient Background Glow --- */}
      <Box
        sx={{
          position: "absolute",
          width: "60vw",
          height: "60vh",
          background: `radial-gradient(circle, ${alpha(accentColor, 0.15)} 0%, transparent 70%)`,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          filter: "blur(80px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      <Stack
        direction={{ xs: "column", lg: "row" }}
        sx={{
          height: "100%",
          gap: { xs: 4, lg: 8 },
          alignItems: "center",
          maxWidth: "1500px",
          mx: "auto",
          width: "100%",
          zIndex: 10,
        }}
      >
        {/* --- Image Showcase --- */}
        <Box
          onClick={() =>
            navigate(`/projects/details/${project?.code || project?._id}`)
          }
          sx={{
            // flex: 1,
            width: { xs: "100%", md: "50%" },
            position: "relative",
            cursor: "pointer",
            "&:hover .image-container": {
              transform: "translateY(-10px) scale(1.02)",
              boxShadow: `0 30px 60px ${alpha(accentColor, 0.3)}`,
              borderColor: alpha(accentColor, 0.6),
            },
          }}
        >
          <Box
            className="image-container"
            sx={{
              borderRadius: "24px",
              overflow: "hidden",
              border: `1px solid ${alpha(theme?.palette?.divider, 0.1)}`,
              background: alpha(theme?.palette?.background?.paper, 0.4),
              backdropFilter: "blur(20px)",
              padding: { xs: 1, md: 2 },
              transition: "all 0.5s cubic-bezier(0.25, 1, 0.5, 1)",
              boxShadow: `0 20px 40px ${alpha(theme?.palette?.common?.black, 0.4)}`,
            }}
          >
            <Box
              component="img"
              src={project?.image}
              alt={project?.title}
              sx={{
                width: "100%",
                height: "auto",
                aspectRatio: "16/10",
                objectFit: "cover",
                borderRadius: "16px",
              }}
            />
          </Box>
        </Box>

        {/* --- Details Container --- */}
        <Stack
          sx={{
            // flex: 1,
            gap: 4,
            width: { xs: "100%", md: "50%" },
          }}
        >
          {/* --- Title & Meta --- */}
          <Stack gap={1}>
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.2rem",
                  color: alpha(theme?.palette?.text?.primary, 0.3),
                  letterSpacing: 2,
                }}
              >
                0{idx + 1}
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  height: "1px",
                  background: alpha(theme?.palette?.divider, 0.2),
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: alpha(theme?.palette?.text?.primary, 0.5),
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                {project?.doneTime}
              </Typography>
            </Stack>

            {/* --- Gradient Title --- */}
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "1.5rem", md: "1.8rem", xl: "2.5rem" },
                fontWeight: 800,
                background: `linear-gradient(135deg, ${accentColor}, ${theme?.palette?.primary?.light})`,
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: 1.1,
                letterSpacing: "-1px",
              }}
            >
              {project?.title}
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                lineHeight: 1.8,
                color: theme?.palette?.text?.secondary,
              }}
            >
              {project?.description}
            </Typography>
          </Stack>

          {/* --- Interactive Data Bento Box (No Stats) --- */}
          <Stack
            direction={{ xs: "column", md: "row" }}
            gap={3}
            sx={{
              background: alpha(theme?.palette?.background?.paper, 0.4),
              backdropFilter: "blur(10px)",
              border: `1px solid ${alpha(theme?.palette?.divider, 0.1)}`,
              borderRadius: "16px",
              p: 3,
            }}
          >
            {/* --- Role & Features --- */}
            <Stack gap={2} sx={{ width: { xs: "100%", md: "50%" } }}>
              <Stack gap={0.5}>
                <Typography
                  variant="caption"
                  sx={{
                    color: theme?.palette?.text?.secondary,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                  }}
                >
                  Role
                </Typography>
                <Stack direction="row" alignItems="center" gap={1}>
                  <CodeIcon fontSize="small" sx={{ color: accentColor }} />
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 700,
                      color: theme?.palette?.text?.primary,
                      textTransform: "capitalize",
                    }}
                  >
                    {project?.myRole || "Full Stack"} Developer
                  </Typography>
                </Stack>
              </Stack>

              {project?.keyFeatures?.length > 0 && (
                <Stack gap={1} sx={{ mt: 1 }}>
                  {project?.keyFeatures?.slice(0, 2)?.map((feature, i) => (
                    <Stack
                      key={i}
                      direction="row"
                      alignItems="center"
                      gap={1.5}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: accentColor,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        noWrap
                        sx={{
                          color: theme?.palette?.text?.secondary,
                          fontSize: "0.85rem",
                        }}
                      >
                        {feature}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>

            <Divider
              orientation="vertical"
              flexItem
              sx={{
                display: { xs: "none", sm: "block" },
                borderColor: alpha(theme?.palette?.divider, 0.1),
              }}
            />

            {/* --- Tech Stack --- */}
            <Stack gap={1.5} sx={{ width: { xs: "100%", md: "50%" } }}>
              <Typography
                variant="caption"
                sx={{
                  color: theme?.palette?.text?.secondary,
                  textTransform: "uppercase",
                  letterSpacing: 1,
                }}
              >
                Tech Stack
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={1}>
                {project?.tech?.map((techItem, i) => (
                  <Box
                    key={i}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "6px",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      background: alpha(accentColor, 0.1),
                      color: accentColor,
                      border: `1px solid ${alpha(accentColor, 0.2)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        background: alpha(accentColor, 0.2),
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    {techItem}
                  </Box>
                ))}
              </Stack>
            </Stack>
          </Stack>

          {/* --- Buttons --- */}
          <Stack direction="row" gap={1} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              href={project?.liveUrl}
              target="_blank"
              startIcon={<OpenInNew />}
              sx={{
                background: `linear-gradient(135deg, ${accentColor}, ${alpha(accentColor, 0.8)})`,
                color: "#fff",
                px: {xs:2 , lg:4},
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize:".8rem" ,
                boxShadow: `0 8px 20px ${alpha(accentColor, 0.3)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-3px)",
                  boxShadow: `0 12px 25px ${alpha(accentColor, 0.5)}`,
                },
              }}
            >
              Live Project
            </Button>

            <Button
              variant="outlined"
              href={project?.githubUrl || "#"}
              target="_blank"
              startIcon={<GitHub />}
              sx={{
                borderColor: alpha(theme?.palette?.divider, 0.2),
                color: theme?.palette?.text?.primary,
                px: {xs:2 , lg:4},
                py: 1.5,
                borderRadius: "8px",
                fontWeight: 600,
                fontSize:".8rem" ,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: alpha(theme?.palette?.divider, 0.05),
                  borderColor: theme?.palette?.text?.primary,
                  transform: "translateY(-3px)",
                },
              }}
            >
              Source Code
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
